import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Volume2, VolumeX, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { track } from "@/lib/api";

function embedUrl(url, loop) {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&playsinline=1${loop ? `&loop=1&controls=0&playlist=${yt[1]}` : ""}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1&muted=1${loop ? "&loop=1&background=1" : ""}`;
  return null;
}

function autoSrc(slot) {
  const c = typeof navigator !== "undefined" ? navigator.connection : null;
  if (c && (c.saveData || /2g/.test(c.effectiveType || ""))) return slot.url_480 || slot.url_720 || slot.url;
  if (c && (c.effectiveType === "3g" || (c.downlink && c.downlink < 5))) return slot.url_720 || slot.url;
  return slot.url;
}

function LoopVideo({ src, poster, className, testid, onFail }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setVisible(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <video ref={ref} data-testid={testid} src={visible ? src : undefined} poster={poster} className={className} muted autoPlay loop playsInline preload="none" onError={onFail} />;
}

export function VideoPlayer({ slot, poster, className = "", testid = "video", alt = "" }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [quality, setQuality] = useState("auto");
  const [ended, setEnded] = useState(false);
  const embed = embedUrl(slot.url, false);
  const src = quality === "720" ? (slot.url_720 || slot.url) : quality === "1080" ? slot.url : autoSrc(slot);
  const useWebm = slot.webm && src === slot.url;

  if (!playing) {
    return (
      <button type="button" data-testid={`${testid}-play`} onClick={() => { setPlaying(true); track("video_play", { slot: testid }); }} className={`relative block w-full h-full ${className}`} aria-label="Play video">
        {poster ? <img src={poster} alt={alt} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full bg-[#121316]" />}
        <span className="absolute inset-0 flex items-center justify-center bg-black/10">
          <span className="h-16 w-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl"><Play size={26} className="text-[#E05624] ml-1" /></span>
        </span>
      </button>
    );
  }
  if (embed) return <iframe title={alt} src={embed} className={`w-full h-full ${className}`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid={testid} />;
  return (
    <div className={`relative w-full h-full bg-black ${className}`}>
      <video key={src} data-testid={testid} poster={poster} className="w-full h-full object-cover" autoPlay muted={muted} playsInline preload="auto" onEnded={() => setEnded(true)} onPlay={() => setEnded(false)}>
        {useWebm && <source src={slot.webm} type="video/webm" />}
        <source src={src} type="video/mp4" />
      </video>
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <select data-testid={`${testid}-quality`} value={quality} onChange={(e) => setQuality(e.target.value)} className="h-8 px-2 rounded-full bg-black/60 text-white text-xs font-bold outline-none">
          <option value="auto">Auto</option><option value="1080">1080p</option><option value="720">720p</option>
        </select>
        <button type="button" data-testid={`${testid}-unmute`} onClick={() => setMuted((m) => !m)} className="h-8 px-3 rounded-full bg-black/60 text-white text-xs font-bold inline-flex items-center gap-1.5">
          {muted ? <><VolumeX size={14} /> {t("unmute")}</> : <><Volume2 size={14} /> Mute</>}
        </button>
      </div>
      {(slot.caption_hi || slot.caption_en) && (
        <div className="absolute bottom-3 left-3 right-3 text-center pointer-events-none">
          {slot.caption_hi && <div className="inline-block bg-black/70 text-white font-bold text-sm sm:text-base px-3 py-1 rounded-lg">{slot.caption_hi}</div>}
          {slot.caption_en && <div className="mt-1"><span className="inline-block bg-black/60 text-white/90 text-xs px-2 py-0.5 rounded">{slot.caption_en}</span></div>}
        </div>
      )}
      {ended && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
          <button type="button" data-testid={`${testid}-end-book`} onClick={() => navigate("/book")} className="inline-flex items-center gap-2 h-12 px-6 rounded-full btn-saffron font-bold">{t("book")} <ArrowRight size={18} /></button>
        </div>
      )}
    </div>
  );
}

export default function MediaSlot({ slot, fallback, mode = "loop", alt = "", className = "", testid = "media" }) {
  const [failed, setFailed] = useState(false);
  const url = (slot?.url || "").trim();
  const poster = slot?.poster;
  if (!url || failed) return poster ? <img src={poster} alt={alt} className={className} loading="lazy" data-testid={`${testid}-fallback`} /> : (fallback || null);
  if (mode === "tap") return <div className={className}><VideoPlayer slot={slot} poster={poster} testid={testid} alt={alt} /></div>;
  const embed = embedUrl(url, true);
  if (embed) return <iframe title={alt} src={embed} className={className} allow="autoplay" data-testid={testid} loading="lazy" />;
  return <LoopVideo src={url} poster={poster} className={className} testid={testid} onFail={() => setFailed(true)} />;
}
